import { useMediaQuery, useTheme } from '@material-ui/core';
import { useCallback, useRef } from 'react';

export const useResponsiveFlags = () => {
  const theme = useTheme();
  const defaultMatchesCacheRef = useRef<Map<string, boolean>>(new Map());

  const getDefaultMatches = useCallback((query: string) => {
    const normalizedQuery = query.replace(/^@media\s*/, '');
    const cache = defaultMatchesCacheRef.current;
    const cachedValue = cache.get(normalizedQuery);
    if (cachedValue !== undefined) {
      return cachedValue;
    }

    const matches =
      typeof window !== 'undefined' ? window.matchMedia(normalizedQuery).matches : false;
    cache.set(normalizedQuery, matches);
    return matches;
  }, []);

  const useStableMediaQuery = (query: string) => {
    return useMediaQuery(query, {
      defaultMatches: getDefaultMatches(query),
      noSsr: true,
    });
  };

  const isPhone = useStableMediaQuery(theme.breakpoints.down('xs')); // <= 599px
  const isTablet = useStableMediaQuery(theme.breakpoints.only('sm')); // 600-959px
  const isDesktop = useStableMediaQuery(theme.breakpoints.up('md')); // >= 960px
  const isPortrait = useStableMediaQuery('(orientation: portrait)');
  const isLandscape = useStableMediaQuery('(orientation: landscape)');
  const isPhonePortrait = isPhone && isPortrait;

  // Deprecated: custom breakpoints, keep for backward compatibility during transition
  const isDesktopLegacy = useStableMediaQuery(theme.breakpoints.up(1000));
  const isMobile = useStableMediaQuery(theme.breakpoints.down(1000));
  const isMobilePortrait = useStableMediaQuery('(max-width:599.95px)');
  const isMediumScreen = useStableMediaQuery(theme.breakpoints.between(600, 1000));
  const isLargeScreen = useStableMediaQuery(theme.breakpoints.up('lg')); // >= 1280px

  return {
    isPhone,
    isTablet,
    isDesktop,
    isPortrait,
    isLandscape,
    isPhonePortrait,
    isDesktopLegacy,
    isMobile,
    isMobilePortrait,
    isMediumScreen,
    isLargeScreen,
  };
};
